import { el } from '../dom.js';
import type { I18n } from '../i18n.js';

/**
 * SEO.3 — researcher-facing content tier. Rich text, indexable, with links
 * to the specification, privacy notice, and citation. Participants do not
 * see this by default — it is reached via a quiet footer link from landing
 * or via direct URL/search.
 *
 * DR-SEO-1: this view is the tension resolution — landing stays minimalist
 * (G1 anti-priming), but researchers and LLM crawlers have a rich page to
 * land on. The `<static-fallback>` section in index.html mirrors this
 * content for no-JS clients (LLM agents).
 */

export interface AboutResearchProps {
  i18n: I18n;
  onBack: () => void;
}

// Pre-registration hash: SHA-256 of the design document as of SPEC_HASH_DATE.
// Regenerate with `shasum -a 256 <design-doc>` whenever the document is
// updated, and bump SPEC_HASH_DATE accordingly.
const SPEC_HASH_SHA256 = '9d9efc54899a69f1150cb3f5cb242b6f4de2bb4f40ad66263ae1dab800364817';
const SPEC_HASH_DATE = '2026-05-11';

export function AboutResearchView({ i18n, onBack }: AboutResearchProps): HTMLElement {
  return el('article', { class: 'research-doc', 'aria-labelledby': 'research-title' }, [
    el('div', { class: 'research-hero' }, [
      el('h1', { id: 'research-title' }, i18n.t('research.heading')),
      el('p', { class: 'lede' }, i18n.t('research.lede')),
    ]),

    el('section', { class: 'research-section' }, [
      el('h2', {}, i18n.t('research.method_heading')),
      el('p', {}, i18n.t('research.method_body')),
    ]),

    el('section', { class: 'research-section' }, [
      el('h2', {}, i18n.t('research.guardrails_heading')),
      el('p', {}, i18n.t('research.guardrails_body')),
    ]),

    el('section', { class: 'research-section' }, [
      el('h2', {}, i18n.t('research.data_heading')),
      el('p', {}, i18n.t('research.data_body')),
    ]),

    el('section', { class: 'research-section' }, [
      el('h2', {}, i18n.t('research.prereg_heading')),
      el('p', {}, i18n.t('research.prereg_body')),
      el('dl', { class: 'research-prereg' }, [
        el('dt', {}, i18n.t('research.prereg_hash_label')),
        el('dd', {},
          el('code', { class: 'research-hash' }, `sha256:${SPEC_HASH_SHA256}`)),
        el('dt', {}, i18n.t('research.prereg_date_label')),
        el('dd', {}, SPEC_HASH_DATE),
      ]),
    ]),

    el('section', { class: 'research-section' }, [
      el('h2', {}, i18n.t('research.resources_heading')),
      el('ul', { class: 'research-resources' }, [
        el('li', {}, [
          el('a', { href: '#privacy', onClick: (ev: Event) => { ev.preventDefault(); onBack(); } },
            i18n.t('research.resources_privacy')),
        ]),
      ]),
    ]),

    el('section', { class: 'research-section' }, [
      el('h2', {}, i18n.t('research.citation_heading')),
      el('blockquote', { class: 'research-citation' }, i18n.t('research.citation_body')),
    ]),

    el('div', { class: 'actions' }, [
      el('div', { class: 'actions-primary' }, [
        el('button', { class: 'secondary', onClick: () => onBack() },
          i18n.t('research.back_to_landing')),
      ]),
    ]),
  ]);
}
